import { useRef, useState } from "react";
import { Camera, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

export function TeacherPhotoManager({ teacherId, photoUrl, name, onChange }) {
  const inputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const initials = (name || "?").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  if (!teacherId) {
    return (
      <p className="border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="teacher-photo-hint">
        Salve o professor primeiro para adicionar uma foto. Formatos: JPEG, PNG ou WebP · máximo 10 MB.
      </p>
    );
  }

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("A imagem excede o limite de 10 MB.");
      e.target.value = "";
      return;
    }
    setPendingFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const { data } = await api.post(`/teachers/${teacherId}/photo`, fd);
      onChange(data.photo_url);
      toast.success("Foto do professor atualizada.");
      setPendingFile(null);
      setPreview(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/teachers/${teacherId}/photo`);
      onChange(null);
      toast.success("Foto removida. O site voltará a exibir as iniciais.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const current = preview || photoUrl;
  return (
    <div className="flex items-center gap-4" data-testid="teacher-photo-manager">
      {current ? (
        <img src={current} alt={name || "Foto do professor"} className="h-20 w-20 border border-border object-cover" data-testid="teacher-photo-preview" />
      ) : (
        <div className="flex h-20 w-20 items-center justify-center border border-border bg-secondary text-lg font-bold text-primary" data-testid="teacher-photo-initials">
          {initials}
        </div>
      )}
      <div className="space-y-2">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="teacher-photo-file" />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="teacher-photo-pick">
            <Camera size={14} className="mr-1" /> {photoUrl ? "Trocar foto" : "Adicionar foto"}
          </Button>
          {pendingFile && (
            <Button type="button" size="sm" onClick={upload} disabled={busy} className="bg-primary text-primary-foreground" data-testid="teacher-photo-upload">
              <Upload size={14} className="mr-1" /> {busy ? "Enviando..." : "Enviar foto"}
            </Button>
          )}
          {photoUrl && !pendingFile && (
            <Button type="button" variant="outline" size="sm" onClick={remove} disabled={busy} data-testid="teacher-photo-remove">
              <Trash2 size={14} className="mr-1 text-destructive" /> Excluir foto
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">JPEG, PNG ou WebP · máximo 10 MB · a imagem é otimizada automaticamente.</p>
      </div>
    </div>
  );
}
